import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  BODY_MAX,
  SUBJECT_MAX,
  channelHasSubject,
  type AdminChannel,
  type CommunicationLocale,
} from "../shared/communications-types";
import { untranslatedLocales } from "../shared/communication-selectors";
import { resolveCommunicationError } from "../shared/resolve-communication-error";
import { useCreateTranslation } from "./use-translations";

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly channel: AdminChannel;
  readonly locales: readonly CommunicationLocale[];
}

export function AddTranslationDialog({ open, onOpenChange, channel, locales }: Props) {
  const { t } = useTranslation();
  const create = useCreateTranslation();
  const needsSubject = channelHasSubject(channel.channel);

  // Only locales not yet translated on this channel — the API would 409
  // TRANSLATION_ALREADY_EXISTS otherwise.
  const availableLocales = useMemo(() => untranslatedLocales(channel, locales), [channel, locales]);

  const [localeCode, setLocaleCode] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setLocaleCode(availableLocales[0]?.code ?? "");
      setSubject("");
      setBody("");
      setError(null);
    }
  }, [open, availableLocales]);

  const trimmedSubject = subject.trim();
  const trimmedBody = body.trim();
  const isValid =
    localeCode.length > 0 &&
    trimmedBody.length > 0 &&
    trimmedBody.length <= BODY_MAX &&
    (!needsSubject || (trimmedSubject.length > 0 && trimmedSubject.length <= SUBJECT_MAX));

  const handleSubmit = async () => {
    if (!isValid || create.isPending) return;
    setError(null);
    try {
      await create.mutateAsync({
        channelId: channel.id,
        input: {
          localeCode,
          // SMS/WhatsApp carry no subject — send null explicitly so the API
          // stores it as a body-only translation.
          subject: needsSubject ? trimmedSubject : null,
          body: trimmedBody,
        },
      });
      onOpenChange(false);
    } catch (err) {
      setError(resolveCommunicationError(err, t));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !create.isPending && onOpenChange(next)}>
      <DialogContent data-testid="communication-add-translation-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t("communications.translations.add.title", {
              channel: channel.channel,
            })}
          </DialogTitle>
          <DialogDescription>{t("communications.translations.add.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {availableLocales.length === 0 && (
            <p
              data-testid="communication-add-translation-no-locales"
              className="rounded-md border border-border bg-muted/30 p-2 text-sm text-muted-foreground"
            >
              {t("communications.translations.add.noAvailableLocales")}
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="add-tr-locale">{t("communications.translations.add.locale")}</Label>
            <select
              id="add-tr-locale"
              data-testid="communication-add-translation-locale-input"
              value={localeCode}
              onChange={(e) => setLocaleCode(e.target.value)}
              disabled={availableLocales.length === 0}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {availableLocales.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.code}
                </option>
              ))}
            </select>
          </div>

          {needsSubject && (
            <div className="space-y-2">
              <Label htmlFor="add-tr-subject">
                {t("communications.translations.subjectField")}
              </Label>
              <Input
                id="add-tr-subject"
                data-testid="communication-add-translation-subject-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={SUBJECT_MAX}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="add-tr-body">{t("communications.translations.bodyField")}</Label>
            <Textarea
              id="add-tr-body"
              data-testid="communication-add-translation-body-input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={BODY_MAX}
              rows={10}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              {t("communications.translations.handlebarsHint")}
            </p>
          </div>

          {error && (
            <p
              data-testid="communication-add-translation-error"
              className="text-sm text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            {t("communications.actions.cancel")}
          </Button>
          <Button
            data-testid="communication-add-translation-submit"
            onClick={handleSubmit}
            disabled={!isValid || create.isPending}
          >
            {create.isPending
              ? t("communications.actions.saving")
              : t("communications.actions.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
